// src/pages/Fintech.tsx
import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
    Container, Typography, Box, Card, CardContent,
    TextField, Button, Slider, Collapse, useTheme, useMediaQuery
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import EcoIcon from '@mui/icons-material/CreditScore';
import ToggleCard from '../components/ToggleCard';

type Transaction = {
    title: string;
    date: string;
    points: number;
    amount: string;
};

const Fintech: React.FC = () => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
    const [sent, setSent] = useState(false);
    const [amount, setAmount] = useState(250);
    const [tab, setTab] = useState<'current' | 'history'>('current');
    const confirmRef = useRef<HTMLDivElement>(null);

    const handleSend = () => setSent(true);

    const data = {
        // for JSON toggle
        earthPointsEarned: 25,
        progressToNextReward: 25,
        totalContribution: {
            mangrovesRestored: { amount: 32, description: 'Mangrove restoration in Indonesia' },
            educationDays: { amount: 12, description: 'Days of school funded in Cambodia' }
        },
        yourMonthlyImpact:
            'Your savings and transactions helped absorbing 394 kg of CO₂!',
        // history-specific
        lifetimePoints: 965,
        recentTransactions: [
            { title: 'Transfer', date: 'Just now', points: 25, amount: '$250.00' },
            { title: 'Online Purchase', date: 'Yesterday', points: 12, amount: '$124.50' },
            { title: 'Monthly Contribution', date: 'Apr 15, 2025', points: 50, amount: '$25.00' },
        ] as Transaction[]
    };

    return (
        <Container maxWidth="lg">
            <Typography variant="h3" sx={{ color: 'rgb(79,111,82)', mb: 1 }}>
                Regenerative Banking
            </Typography>
            <Typography variant="subtitle1" sx={{ mb: 4 }}>
                Every transaction contributes to a healthier planet and thriving communities with our API
            </Typography>

            <Box display="flex" flexDirection={isDesktop ? 'row' : 'column'} gap={4}>
                {/* Left Panel */}
                <Box flex={1}>
                    <Card>
                        <CardContent>
                            <Typography
                                variant="h5"
                                sx={{ mb: 2, fontWeight: 700, display: 'flex', alignItems: 'center' }}
                            >
                                <AccountBalanceWalletIcon sx={{ mr: 1, color: 'rgb(101,183,65)' }} />
                                Digital Wallet
                            </Typography>

                            <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <TextField fullWidth disabled label="Recipient" value='John Doe' />

                                <Box>
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography>Amount (USD)</Typography>
                                        <Typography fontWeight={700}>${amount}</Typography>
                                    </Box>
                                    <Slider
                                        value={amount}
                                        disabled={true}
                                        onChange={(_, v) => setAmount(v as number)}
                                        min={10}
                                        max={1000}
                                    />
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography variant="body2">$10</Typography>
                                        <Typography variant="body2">$1000</Typography>
                                    </Box>
                                </Box>

                                <Card sx={{ backgroundColor: 'rgb(239,253,219)' }}>
                                    <CardContent>
                                        <Typography variant="subtitle1" fontWeight={700}>
                                            Regenerative Impact
                                        </Typography>
                                        <Typography variant="body2">
                                            1% of each transaction funds mangrove restoration in Indonesia
                                            and 0.5% supports education in Cambodia
                                        </Typography>
                                    </CardContent>
                                </Card>

                                <TextField fullWidth disabled label="Add a note (optional)" value='' />

                                <Button
                                    variant="contained"
                                    fullWidth
                                    sx={{ py: 1.5, fontWeight: 700 }}
                                    disabled={sent}
                                    onClick={handleSend}
                                >
                                    Confirm Transfer
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                {/* Right Panel */}
                <Box flex={1} ref={confirmRef} sx={{ scrollMarginTop: '80px' }}>
                    <Collapse
                        in={sent}
                        timeout={600}
                        unmountOnExit
                        onEntered={() => confirmRef.current?.scrollIntoView({ behavior: 'smooth' })}
                    >
                        <Card>
                            <CardContent>
                                {/* Header + JSON toggle */}
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Typography
                                        variant="h5"
                                        sx={{ fontWeight: 700, display: 'flex', alignItems: 'center' }}
                                    >
                                        <EcoIcon sx={{ mr: 1, color: 'rgb(101,183,65)' }} />
                                        Payment Confirmed
                                    </Typography>
                                </Box>
                                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                    Your payment is confirmed! See how your transaction is making a difference.
                                </Typography>
                                <ToggleCard title="&nbsp;" code={data}>

                                    {/* Tabs */}
                                    <Box display="flex" borderRadius={1} overflow="hidden" mb={3}>
                                        {(['current', 'history'] as const).map(key => (
                                            <Box
                                                key={key}
                                                flex={1}
                                                textAlign="center"
                                                py={1}
                                                onClick={() => setTab(key)}
                                                sx={{
                                                    cursor: 'pointer',
                                                    backgroundColor:
                                                        tab === key ? 'white' : 'rgb(239,253,219)',
                                                    color:
                                                        tab === key ? 'text.primary' : 'text.secondary',
                                                }}
                                            >
                                                {key === 'current' ? 'Current Impact' : 'Impact History'}
                                            </Box>
                                        ))}
                                    </Box>

                                    {/* Current Impact */}
                                    {tab === 'current' && (
                                        <>
                                            {/* Earth Points */}
                                            <Box
                                                sx={{
                                                    backgroundColor: 'rgb(239,253,219)',
                                                    p: 2,
                                                    borderRadius: 1,
                                                    mb: 3,
                                                }}
                                            >
                                                <Box display="flex" justifyContent="space-between">
                                                    <Typography fontWeight={700}>Earth Points Earned <p style={{ fontWeight: 'normal', fontSize: '12px', marginBottom: '-8px' }}>Progress to next reward</p></Typography>
                                                    <Typography fontWeight={700}>+{data.earthPointsEarned} pts</Typography>
                                                </Box>
                                                <Box display="flex" alignItems="center" mt={1}>
                                                    <Box
                                                        flex={1}
                                                        height={8}
                                                        borderRadius={4}
                                                        sx={{
                                                            background:
                                                                'linear-gradient(90deg, #7FBF4D 0%, #7FBF4D 25%, #44533E 25%, #44533E 100%)',
                                                        }}
                                                    />
                                                    <Typography ml={1} fontSize="0.875rem">
                                                        {data.progressToNextReward}%
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {/* Total Contribution */}
                                            <Typography variant="subtitle1" fontWeight={700} mb={1}>
                                                Your Total Contribution
                                            </Typography>
                                            <Box display="flex" gap={2} mb={3}>
                                                <Card variant="outlined" sx={{ flex: 1 }}>
                                                    <CardContent>
                                                        <Typography fontSize="2rem" fontWeight={700}>
                                                            {data.totalContribution.mangrovesRestored.amount}
                                                        </Typography>
                                                        <Typography>🌱 Mangroves Restored</Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {data.totalContribution.mangrovesRestored.description}
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                                <Card variant="outlined" sx={{ flex: 1 }}>
                                                    <CardContent>
                                                        <Typography fontSize="2rem" fontWeight={700}>
                                                            {data.totalContribution.educationDays.amount}
                                                        </Typography>
                                                        <Typography>📚 Education Days</Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {data.totalContribution.educationDays.description}
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                            </Box>

                                            {/* Monthly Message */}
                                            <Box
                                                sx={{ backgroundColor: 'rgb(239,253,219)', p: 2, borderRadius: 1 }}
                                            >
                                                <Typography variant="subtitle1" fontWeight={700} mb={1}>
                                                    Your impact this year
                                                </Typography>
                                                <Typography variant="body2">
                                                    {data.yourMonthlyImpact}
                                                </Typography>
                                            </Box>
                                        </>
                                    )}

                                    {/* Impact History */}
                                    {tab === 'history' && (
                                        <>
                                            {/* Lifetime Impact */}
                                            <Box
                                                sx={{
                                                    backgroundColor: 'rgb(239,253,219)',
                                                    p: 2,
                                                    borderRadius: 1,
                                                    mb: 3,
                                                }}
                                            >
                                                <Typography fontWeight={700}>Lifetime Impact</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Earth points earned since joining
                                                </Typography>
                                                <Typography
                                                    variant="h4"
                                                    sx={{ mt: 1, fontWeight: 700, color: 'rgb(79,111,82)' }}
                                                >
                                                    {data.lifetimePoints}
                                                </Typography>
                                            </Box>

                                            {/* Recent Transactions */}
                                            <Typography variant="subtitle1" fontWeight={700} mb={1}>
                                                Recent Transactions
                                            </Typography>
                                            <Box display="flex" flexDirection="column" gap={2}>
                                                {data.recentTransactions.map((tx, i) => (
                                                    <Card key={i} variant="outlined">
                                                        <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Box>
                                                                <Typography fontWeight={700}>{tx.title}</Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {tx.date}
                                                                </Typography>
                                                            </Box>
                                                            <Box textAlign="right">
                                                                <Typography fontWeight={700}>+{tx.points} pts</Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {tx.amount}
                                                                </Typography>
                                                            </Box>
                                                        </CardContent>
                                                    </Card>
                                                ))}
                                            </Box>
                                        </>
                                    )}
                                </ToggleCard>
                                {/* Tracking CTA */}
                                <Box
                                    component={Link}
                                    to="/engagement"
                                    sx={{
                                        textDecoration: 'none',     // remove underline
                                        color: 'inherit',           // inherit text colour
                                        backgroundColor: 'rgb(239,253,219)',
                                        p: 2,
                                        borderRadius: 1,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: 'rgb(227,244,204)',
                                            textDecoration: 'none'    // ensure no underline on hover
                                        }
                                    }}
                                >
                                    <Typography fontWeight={700}>Track Your Impact <span style={{ fontWeight: 400 }}>- get exciting updates and stories!</span></Typography>
                                    <Typography>{'→'}</Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Collapse>
                </Box>
            </Box>
        </Container>
    );
};

export default Fintech;
